import React, { useState } from "react";
import {
  Button,
  Group,
  Paper,
  Stack,
  Text,
  FileInput,
  Alert,
  Progress,
} from "@mantine/core";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useUploadTorDocument } from "../hooks/useTorDocuments";

interface TorDocumentUploadProps {
  tenderId: string;
  onUploadSuccess?: () => void;
}

export const TorDocumentUpload: React.FC<TorDocumentUploadProps> = ({
  tenderId,
  onUploadSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadDocument = useUploadTorDocument(tenderId);

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setUploadProgress(0);
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return "Invalid file type. Only PDF, DOC, and DOCX files are allowed.";
    }

    if (file.size > maxSize) {
      return "File size exceeds 10MB limit.";
    }

    return null;
  };

  const handleUpload = async () => {
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      return;
    }

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = (e.target?.result as string).split(",")[1];

        const uploadData = {
          fileName: file.name,
          base64File: base64,
          mimeType: file.type,
          fileSize: file.size,
        };

        await uploadDocument.mutateAsync(uploadData);
        setFile(null);
        setUploadProgress(0);
        onUploadSuccess?.();
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      default:
        return "📎";
    }
  };

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Text size="lg" fw={500}>
          Upload TOR Document
        </Text>

        <Text size="sm" color="dimmed">
          Upload a PDF, DOC, or DOCX file containing the Terms of Reference.
          Maximum file size: 10MB.
        </Text>

        <FileInput
          placeholder="Choose TOR document"
          accept=".pdf,.doc,.docx"
          value={file}
          onChange={handleFileChange}
          leftSection={<IconUpload size={16} />}
          error={file ? validateFile(file) : null}
        />

        {file && (
          <Alert
            icon={getFileIcon(file.name)}
            title="Selected File"
            color="blue"
            variant="light"
          >
            <Text size="sm">
              <strong>Name:</strong> {file.name}
            </Text>
            <Text size="sm">
              <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
            </Text>
            <Text size="sm">
              <strong>Type:</strong> {file.type}
            </Text>
          </Alert>
        )}

        {uploadDocument.isPending && (
          <Stack gap="xs">
            <Text size="sm">Uploading document...</Text>
            <Progress value={uploadProgress} size="sm" />
          </Stack>
        )}

        <Group justify="right">
          {file && (
            <Button
              variant="light"
              color="red"
              leftSection={<IconX size={16} />}
              onClick={() => setFile(null)}
            >
              Clear
            </Button>
          )}

          <Button
            leftSection={<IconUpload size={16} />}
            onClick={handleUpload}
            loading={uploadDocument.isPending}
            disabled={!file || uploadDocument.isPending}
          >
            Upload Document
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
